import { useState } from 'react';
import { useParams, Link } from 'react-router';
import {
  ChevronLeftIcon,
  MapPinIcon,
  ClockIcon,
  BellIcon,
  Loader2Icon,
  CheckCircle2Icon,
  AlertCircleIcon,
  ThumbsUpIcon,
  CameraIcon,
  SparklesIcon,
  MessageSquareIcon,
  SendIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { StatusTimeline } from '@/components/report/status-timeline';
import {
  getStatusConfig,
  formatDateTime,
  formatRelativeTime,
  SEVERITY_LABELS,
  SEVERITY_COLORS
} from '@/lib/report-helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useNudgeReport,
  useUpvoteReport,
  useAddReportComment,
  getTrackReportQueryOptions
} from '@cdf/api-client-react';
import { useSession } from '@/components/auth/user-provider';
import { ReportLocationMap } from '@/components/map/report-location-map';
import type { PhotoLocation } from '@/components/map/use-photo-markers';
import { cn } from '@/lib/utils';

export function TrackingPage() {
  const { token } = useParams<{ token: string }>();
  const [nudgeMessage, setNudgeMessage] = useState<string | null>(null);
  const [nudgeError, setNudgeError] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const { session } = useSession();
  const queryClient = useQueryClient();

  const { data: report, isLoading, isError } = useQuery({
    ...getTrackReportQueryOptions(token ?? ''),
    enabled: !!token
  });

  const nudgeMutation = useNudgeReport();
  const upvoteMutation = useUpvoteReport();
  const commentMutation = useAddReportComment();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-4 px-4 text-center">
        <AlertCircleIcon className="h-12 w-12 text-muted-foreground" />
        <div>
          <h1 className="text-lg font-semibold">Report not found</h1>
          <p className="text-sm text-muted-foreground">
            This tracking link is invalid or the report may have been removed.
          </p>
        </div>
        <Button asChild>
          <Link to="/report">Report a New Issue</Link>
        </Button>
      </div>
    );
  }

  const cfg = getStatusConfig(report.status);
  const canNudge = !cfg.terminal;

  const handleNudge = () => {
    setNudgeError(null);
    setNudgeMessage(null);
    nudgeMutation.mutate({ token: token ?? '' }, {
      onSuccess: (res) => {
        setNudgeMessage(res.message ?? 'Nudge sent successfully.');
      },
      onError: (err: unknown) => {
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to send nudge. Please try again later.';
        setNudgeError(msg);
      }
    });
  };

  const handleUpvote = () => {
    upvoteMutation.mutate({ token: token ?? '' });
  };

  const isOwner = report?.isOwner ?? false;
  const isSignedIn = session.status === 'signedIn';

  const handleAddComment = () => {
    setCommentError(null);
    commentMutation.mutate(
      { token: token ?? '', data: { body: commentText.trim() } },
      {
        onSuccess: () => {
          setCommentText('');
          void queryClient.invalidateQueries({
            queryKey: getTrackReportQueryOptions(token ?? '').queryKey
          });
        },
        onError: (err: unknown) => {
          const msg = err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to post comment. Please try again later.';
          setCommentError(msg);
        }
      }
    );
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col bg-background">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Button variant="ghost" size="icon" asChild className="h-8 w-8">
          <Link to="/report">
            <ChevronLeftIcon className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-lg font-semibold">Report Status</h1>
      </header>

      <main className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {/* Photo */}
        {report.photoUrl && (
          <img
            src={report.photoUrl}
            alt="Reported issue"
            className="h-56 w-full rounded-lg object-cover"
          />
        )}

        {/* Map with photo marker */}
        {report.latitude != null && report.longitude != null && (
          <ReportLocationMap
            latitude={report.latitude}
            longitude={report.longitude}
            photoLocations={
              report.photoUrl
                ? [{
                    id: 'report-photo',
                    url: report.photoUrl,
                    latitude: report.latitude,
                    longitude: report.longitude,
                    label: report.categoryName ?? 'Report photo',
                    timestamp: report.createdAt,
                  } satisfies PhotoLocation]
                : []
            }
          />
        )}

        {/* Status summary */}
        <Card>
          <CardContent className="space-y-3 py-4">
            <div className="flex items-center justify-between">
              <Badge className={cn('text-xs', cfg.badgeClass)}>{cfg.label}</Badge>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(report.updatedAt)}
              </span>
            </div>
            <div>
              <h2 className="font-medium">{report.customTypeLabel ? `Other — ${report.customTypeLabel}` : (report.categoryName ?? 'Issue')}</h2>
              {report.description && (
                <p className="mt-1 text-sm text-muted-foreground">{report.description}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPinIcon className="h-3.5 w-3.5" />
                {report.addressText ?? `${report.latitude?.toFixed(4)}, ${report.longitude?.toFixed(4)}`}
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3.5 w-3.5" />
                {formatDateTime(report.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {report.severity && (
                <Badge variant="outline" className={cn('text-xs', SEVERITY_COLORS[report.severity])}>
                  {SEVERITY_LABELS[report.severity] ?? report.severity}
                </Badge>
              )}
              {report.tenantName && (
                <Badge variant="outline" className="text-xs">
                  {report.tenantName}
                </Badge>
              )}
              {(report.upvoteCount ?? 0) > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ThumbsUpIcon className="h-3 w-3" />
                  {report.upvoteCount} support{(report.upvoteCount ?? 0) === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* AI detection summary */}
        {report.aiDetection && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="space-y-2 py-3">
              <div className="flex items-center gap-1.5">
                <SparklesIcon className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">AI Photo Analysis</span>
                {report.aiDetection.confidence != null && (
                  <Badge variant="secondary" className="text-xs">
                    {Math.round(report.aiDetection.confidence * 100)}% confident
                  </Badge>
                )}
              </div>
              <div className="space-y-1 text-sm">
                {report.aiDetection.assetType && (
                  <div>
                    <span className="text-muted-foreground">Asset type:</span>{' '}
                    <span className="font-medium">{report.aiDetection.assetType}</span>
                  </div>
                )}
                {report.aiDetection.problemType && (
                  <div>
                    <span className="text-muted-foreground">Problem:</span>{' '}
                    <span className="font-medium">{report.aiDetection.problemType}</span>
                  </div>
                )}
                {report.aiDetection.severity && (
                  <div>
                    <span className="text-muted-foreground">Severity:</span>{' '}
                    <span className="font-medium">{report.aiDetection.severity}</span>
                  </div>
                )}
                {report.aiDetection.description && (
                  <p className="text-xs text-muted-foreground">{report.aiDetection.description}</p>
                )}
              </div>
              {report.matchedAsset && (
                <div className="rounded-md bg-background/80 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">Matched asset owner:</span>{' '}
                  <span className="font-medium">{report.matchedAsset.ownerTenantName ?? 'Unknown'}</span>
                  {report.matchedAsset.ownerTenantType && (
                    <Badge variant="outline" className="ml-1 text-xs">
                      {report.matchedAsset.ownerTenantType === 'council' ? 'Council' : 'Service Provider'}
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Timeline */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Status Timeline</h3>
          <StatusTimeline entries={report.timeline ?? []} />
        </div>

        {/* Public comments */}
        {(report.comments?.length ?? 0) > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Updates</h3>
            <div className="space-y-2">
              {report.comments?.map((c) => (
                <Card key={c.id}>
                  <CardContent className="py-3">
                    <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                      {c.authorType === 'reporter' ? (
                        <Badge variant="secondary" className="text-xs">Reporter</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Council</Badge>
                      )}
                      {c.authorDisplayName && (
                        <span>{c.authorDisplayName}</span>
                      )}
                      <span>{formatRelativeTime(c.createdAt)}</span>
                    </div>
                    <p className="text-sm">{c.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Comment box for signed-in owners */}
        {isOwner && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Add an update</h3>
            <Textarea
              placeholder="Share an update about this report (e.g. the issue has changed, or more detail about the location)..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={3}
              maxLength={2000}
            />
            {commentError && (
              <p className="text-sm text-destructive">{commentError}</p>
            )}
            <Button
              size="sm"
              disabled={!commentText.trim() || commentMutation.isPending}
              onClick={handleAddComment}
            >
              {commentMutation.isPending ? (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <SendIcon className="mr-2 h-4 w-4" />
              )}
              Post update
            </Button>
          </div>
        )}

        {/* Sign-in prompt to comment (signed-out or non-owner signed-in) */}
        {!isOwner && (report.comments?.length ?? 0) === 0 && !isSignedIn && (
          <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            <MessageSquareIcon className="mx-auto mb-2 h-5 w-5" />
            Sign in with your work account after submitting to add updates to your reports.
          </div>
        )}

        <Separator />

        {/* Actions */}
        <div className="space-y-2">
          {canNudge && (
            <>
              <Button
                variant="outline"
                className="w-full"
                disabled={nudgeMutation.isPending}
                onClick={handleNudge}
              >
                {nudgeMutation.isPending ? (
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <BellIcon className="mr-2 h-4 w-4" />
                )}
                Nudge the authority
              </Button>
              {nudgeMessage && (
                <p className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2Icon className="h-4 w-4" />
                  {nudgeMessage}
                </p>
              )}
              {nudgeError && (
                <p className="text-sm text-destructive">{nudgeError}</p>
              )}
              <p className="text-xs text-muted-foreground">
                You can nudge once every 24 hours if the report seems stalled.
              </p>
            </>
          )}
          <Button
            variant="ghost"
            className="w-full"
            disabled={upvoteMutation.isPending}
            onClick={handleUpvote}
          >
            <ThumbsUpIcon className="mr-2 h-4 w-4" />
            {upvoteMutation.isSuccess ? 'Thanks for your support!' : 'Add my support'}
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/report">
              <CameraIcon className="mr-2 h-4 w-4" />
              Report Another Issue
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
